// (c) chaehongee. Confidential and proprietary.

import { useEffect, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { useAuth } from '../../store/hooks/useAuth'
import {
  changeSubscriptionTier,
  createSubscriptionCheckout,
  getSubscriptionStatus,
  getSubscriptionTiers,
  type SubscriptionTierInfo,
} from '../../services/billing'
import { setCheckoutReturnTo } from '../../services/checkoutReturn'
import { formatPriceTag } from '../../i18n/formatters'
import PrimaryButton from '../PrimaryButton/PrimaryButton'
import styles from './PricingSection.module.css'

type PricingSectionProps = {
  onSignUp?: () => void
}

type Interval = 'monthly' | 'annual'

// Display metadata for paid tiers; prices come from the API.
const TIER_META: Record<string, { name: string; accent: boolean }> = {
  explorer: { name: 'Explorer', accent: false },
  pro: { name: 'Pro', accent: true },
  studio: { name: 'Studio', accent: false },
}

const TIER_ORDER: Record<string, number> = {
  free: 0,
  explorer: 1,
  pro: 2,
  studio: 3,
}

// Rendered until the tiers API responds (and if it fails).
// Mirrors the USD prices in subscription_tiers.py (cents).
const FALLBACK_TIERS: SubscriptionTierInfo[] = [
  {
    tier: 'explorer',
    monthly_credits: 15_000,
    price_monthly: 400,
    price_annual: 3_840,
    currency: 'usd',
    topup_bonus_pct: 0,
    max_rollover_credits: 45_000,
  },
  {
    tier: 'pro',
    monthly_credits: 60_000,
    price_monthly: 1_900,
    price_annual: 18_240,
    currency: 'usd',
    topup_bonus_pct: 0.1,
    max_rollover_credits: 180_000,
  },
  {
    tier: 'studio',
    monthly_credits: 200_000,
    price_monthly: 4_900,
    price_annual: 47_040,
    currency: 'usd',
    topup_bonus_pct: 0.1,
    max_rollover_credits: 600_000,
  },
]

// Backend prices are in the smallest currency unit (cents/won).
function toMajorUnits(amount: number, currency: string): number {
  return currency === 'krw' ? amount : amount / 100
}

function formatPrice(amount: number, currency: string): string {
  return formatPriceTag(toMajorUnits(amount, currency), currency)
}

function formatAnnualMonthly(annualAmount: number, currency: string): string {
  return formatPriceTag(toMajorUnits(annualAmount, currency) / 12, currency)
}

export function PricingSection({ onSignUp }: PricingSectionProps): ReactElement {
  const { t } = useTranslation('pricing')
  const { user } = useAuth()
  const [interval, setInterval] = useState<Interval>('monthly')
  const [tiers, setTiers] = useState<SubscriptionTierInfo[]>(FALLBACK_TIERS)
  const [currentTier, setCurrentTier] = useState<string | null>(null)
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false)
  const [loadingTier, setLoadingTier] = useState<string | null>(null)

  useEffect(() => {
    // Public endpoint: anonymous visitors get regional pricing too.
    getSubscriptionTiers()
      .then((res) => {
        const paid = res.tiers
          .filter((tier) => TIER_META[tier.tier])
          .sort(
            (a, b) => (TIER_ORDER[a.tier] ?? 99) - (TIER_ORDER[b.tier] ?? 99),
          )
        if (paid.length > 0) setTiers(paid)
        setCurrentTier(res.current_tier)
      })
      .catch(() => {}) // keep USD fallback
  }, [user])

  useEffect(() => {
    if (!user) return
    getSubscriptionStatus()
      .then((res) => setHasActiveSubscription(res.status !== 'none'))
      .catch(() => {})
  }, [user])

  const ctaLabel = (tierDef: SubscriptionTierInfo): string => {
    if (!user) return t('getStarted')
    if (!hasActiveSubscription) return t('subscribe')
    const cur = TIER_ORDER[currentTier ?? 'free'] ?? 0
    const target = TIER_ORDER[tierDef.tier] ?? 0
    return target > cur ? t('upgrade') : t('downgrade')
  }

  const handleCta = async (tierDef: SubscriptionTierInfo) => {
    if (!user) {
      if (onSignUp) {
        onSignUp()
      } else {
        window.dispatchEvent(new CustomEvent('show:login'))
      }
      return
    }

    if (currentTier === tierDef.tier) {
      window.location.href = '/dashboard?tab=billing'
      return
    }

    setLoadingTier(tierDef.tier)
    try {
      if (hasActiveSubscription) {
        await changeSubscriptionTier(tierDef.tier, interval)
        window.location.href = '/dashboard?tab=billing'
      } else {
        setCheckoutReturnTo()
        const res = await createSubscriptionCheckout(tierDef.tier, interval)
        window.location.href = res.checkout_url
      }
    } catch (err) {
      console.error('Subscription action failed', err)
      setLoadingTier(null)
    }
  }

  return (
    <section className={styles.pricingSection}>
      <div className={styles.maxWidth}>
        <div className={styles.header}>
          <div className={styles.freeBanner}>{t('freeBanner')}</div>
          <h2 className={styles.title}>{t('title')}</h2>
          <p className={styles.subtitle}>{t('subtitle')}</p>
        </div>

        <div className={styles.toggleWrapper}>
          <div className={styles.togglePill}>
            <button
              className={styles.toggleOption}
              data-active={interval === 'monthly'}
              onClick={() => setInterval('monthly')}
            >
              {t('monthly')}
            </button>
            <button
              className={styles.toggleOption}
              data-active={interval === 'annual'}
              onClick={() => setInterval('annual')}
            >
              {t('annual')}
              <span className={styles.saveBadge}>{t('save20')}</span>
            </button>
          </div>
        </div>

        <div className={styles.cardsGrid}>
          {tiers.map((tierDef) => {
            const meta = TIER_META[tierDef.tier]
            const isCurrent = currentTier === tierDef.tier
            const price =
              interval === 'monthly'
                ? formatPrice(tierDef.price_monthly, tierDef.currency)
                : formatAnnualMonthly(tierDef.price_annual, tierDef.currency)
            const period = t('mo')
            const bonusPct = Math.round(tierDef.topup_bonus_pct * 100)

            const features: string[] = [
              t('credits', { amount: tierDef.monthly_credits.toLocaleString() }),
              t('allModels'),
              t('allFeatures'),
              t('interactiveTools'),
            ]
            if (bonusPct > 0) {
              features.push(t('topupBonus', { pct: bonusPct }))
            }
            if (tierDef.tier === 'studio') {
              features.push(t('priorityQueue'))
            }

            return (
              <div key={tierDef.tier} className={styles.card}>
                {meta.accent && (
                  <div className={styles.popularBadge}>{t('popular')}</div>
                )}

                <div className={styles.tierName}>{meta.name}</div>

                <div className={styles.price}>
                  <span className={styles.priceAmount}>{price}</span>
                  <span className={styles.pricePeriod}>{period}</span>
                </div>

                {interval === 'annual' && (
                  <div className={styles.creditLine}>
                    {formatPrice(tierDef.price_annual, tierDef.currency)}{t('yr')}
                  </div>
                )}

                <div className={styles.creditLine}>
                  {t('credits', { amount: tierDef.monthly_credits.toLocaleString() })}
                </div>

                <hr className={styles.divider} />

                <ul className={styles.featureList}>
                  {features.map((feat) => (
                    <li key={feat} className={styles.featureItem}>
                      <Check className={styles.featureCheck} />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>

                <div className={styles.ctaWrapper}>
                  {isCurrent ? (
                    <div className={styles.currentPlanBadge}>{t('currentPlan')}</div>
                  ) : meta.accent ? (
                    <PrimaryButton
                      onClick={() => void handleCta(tierDef)}
                      loading={loadingTier === tierDef.tier}
                      fullWidth
                    >
                      {ctaLabel(tierDef)}
                    </PrimaryButton>
                  ) : (
                    <button
                      className={styles.ctaSecondary}
                      onClick={() => void handleCta(tierDef)}
                      disabled={loadingTier === tierDef.tier}
                    >
                      {ctaLabel(tierDef)}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <p className={styles.topupCallout}>{t('topupCallout')}</p>

        <div className={styles.faq}>
          <h3 className={styles.faqTitle}>{t('faqTitle')}</h3>
          {([
            ['faqWhatAreCredits', 'faqWhatAreCreditsAnswer'],
            ['faqCanICancel', 'faqCanICancelAnswer'],
            ['faqRollover', 'faqRolloverAnswer'],
            ['faqTopup', 'faqTopupAnswer'],
          ] as const).map(([q, a]) => (
            <div key={q} className={styles.faqItem}>
              <p className={styles.faqQuestion}>{t(q)}</p>
              <p className={styles.faqAnswer}>{t(a)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
